import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ContentContainerType } from 'types/enums';
import { ChakraEmptyList, ChakraFabButton } from 'app/components/Core';
import { ReactComponent as SplitsIcon } from 'resources/images/split.svg';
import { ContentContainer } from 'app/components/Core/ChakraContentContainer';
import { ReactComponent as AddIcon } from 'resources/images/add.svg';
import { useAppDispatch, useAppSelector } from 'app/store';
import {
  getLatestSplit,
  getLatestWorkSplitLink,
} from 'app/store/SongList/selectors';
import {
  acceptSplitEffect,
  downloadSplitPdfEffect,
  fetchContributionClassesRolesDDLEffect,
  fetchLatestSplitEffect,
} from 'app/store/SongList/effects';
import { resetLatestSplitAction } from 'app/store/SongList/slice';
import { getCurrentProfileGuid } from 'app/store/Profile/selectors';
import { SplitMemberStatus } from 'types/splits.types';
import { ChakraConfirmationDialog } from 'app/components/Dialogs/ChakraConfirmationDialog';
import { useContributionRolesDDL, useResource } from 'app/utils/hooks';
import { NewSplitOverlay } from '../NewSplitOverlay';
import { LatestSplitList } from '../LatestSplitList';

export const CurrentSplit = () => {
  const dispatch = useAppDispatch();
  const [showOverlay, setShowOverlay] = useState(false);
  const [showInvalidSplit, setShowInvalidSplit] = useState(false);
  const [showNewCreditMessage, setShowNewCreditMessage] = useState(false);
  const latestSplitLink = useAppSelector(getLatestWorkSplitLink);
  const currentUserGuid = useAppSelector(getCurrentProfileGuid);
  const [split, latestSplitLoading] = useResource(
    fetchLatestSplitEffect,
    getLatestSplit,
    { useCache: false, shouldFetch: Boolean(latestSplitLink) }
  );

  const noSplit = !split && !latestSplitLink;
  const contributionRolesDDL = useContributionRolesDDL();

  const showFab = useMemo(() => {
    const memberFound = split?.members.find(
      (member) => member.memberIdentifier === currentUserGuid
    );
    return memberFound?.status !== SplitMemberStatus.PROPOSED;
  }, [split, currentUserGuid]);

  const downloadSplitPdf = useCallback(async () => {
    try {
      await dispatch(
        downloadSplitPdfEffect({ songGuid: split?.songGuid })
      ).unwrap();
    } catch {
      // proceed
    }
  }, [dispatch, split?.songGuid]);

  useEffect(() => {
    dispatch(fetchContributionClassesRolesDDLEffect());
    return () => {
      dispatch(resetLatestSplitAction());
    };
  }, [dispatch]);

  useEffect(() => {
    setShowInvalidSplit(split?.valid === false);

    const shouldShowCreditMessage = Boolean(
      split && split.valid && split?.hasNewCredits
    );
    setShowNewCreditMessage(shouldShowCreditMessage);
  }, [split]);

  return (
    <>
      <ContentContainer
        type={ContentContainerType.LIST}
        isViewContent
        centerContent={noSplit}
      >
        {latestSplitLoading || !noSplit ? (
          <LatestSplitList
            isLoading={latestSplitLoading}
            split={split}
            proposeNewSplit={() => setShowOverlay(true)}
            acceptSplit={() => dispatch(acceptSplitEffect())}
            downloadSplitPdf={downloadSplitPdf}
            contributionRolesDDL={contributionRolesDDL}
            currentUserGuid={currentUserGuid}
          />
        ) : (
          <ChakraEmptyList
            icon={<SplitsIcon />}
            title="No Split"
            primaryText="Create a new split to get started"
          />
        )}
        {showFab && (
          <ChakraFabButton
            aria-label="new split"
            data-testid="new-split-button"
            onClick={() => setShowOverlay(true)}
            leftIcon={<AddIcon />}
          >
            New split
          </ChakraFabButton>
        )}
      </ContentContainer>
      {showOverlay && <NewSplitOverlay onClose={() => setShowOverlay(false)} />}
      <ChakraConfirmationDialog
        isOpen={showInvalidSplit}
        title="Invalid Split"
        message="A songwriter has been removed from the song, do you want to propose a new split?"
        handleAction={() => {
          setShowOverlay(true);
          setShowInvalidSplit(false);
        }}
        handleClose={() => {
          setShowInvalidSplit(false);
        }}
        cancelButtonText="Not now"
        actionButtonText="New split"
      />
      <ChakraConfirmationDialog
        isOpen={showNewCreditMessage}
        title="New songwriter added"
        message="A songwriter has been added to the song, do you want to propose a new split?"
        handleAction={() => {
          setShowOverlay(true);
          setShowNewCreditMessage(false);
        }}
        handleClose={() => {
          setShowNewCreditMessage(false);
        }}
        cancelButtonText="Not now"
        actionButtonText="New split"
      />
    </>
  );
};
